// SPDX-FileCopyrightText: Szilárd Hajba
// SPDX-License-Identifier: LGPL-3.0-or-later

import {
	ActionSheet,
	ActionSheetDivider,
	ActionSheetItem,
	ActionSheetSubItem,
	absChannel,
	IcDownload,
	IcDuplicate,
	IcPin,
	IcRefresh,
	IcRename,
	IcShare,
	IcView,
	Menu,
	MenuDivider,
	MenuItem,
	SubMenuItem,
	useAuth,
	useIsMobile,
	useToast
} from '@cloudillo/react'
import { useAtom, useStore } from 'jotai'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { activeContextAtom, useCurrentContextIdTag } from '../../../context/index.js'
import { getIcon } from '../../../icon-registry.js'
import {
	IcDeleteForever,
	IcHand,
	IcOpen,
	IcOpenWith,
	IcRestore,
	IcStar,
	IcTrash,
	IcUnpin,
	IcUnstar
} from '../../../icons.js'
import { getHandlersForContentType } from '../../../manifest-registry.js'
import { type FileHandItem, HandTypeConflictError, pickUp } from '../../../state/hand.js'
import { fly, handTargetElAtom, prefersReducedMotion } from '../../../state/hand-fly.js'
import { triggerFileDownload } from '../../viewer/MediaViewer.js'
import type { File, FileOps, ViewMode } from '../types.js'
import { isFileProcessing, MANAGED_FOLDER_ID } from '../types.js'
import {
	canManageFile,
	canManageShares,
	canWrite,
	getVisibilityDropdownOptions,
	getVisibilityIcon,
	getVisibilityOption,
	isCrossOwnerFile,
	resolveAccessLevel,
	toAppAccess
} from '../utils.js'

export interface ContextMenuPosition {
	x: number
	y: number
}

interface PickUpOpts {
	activeContextIdTag: string
	authIdTag: string | undefined
	scopeRoles: string[]
	selectedFiles: File[]
	inTrash: boolean
	isManagedView: boolean
	store: ReturnType<typeof useStore>
	toast: ReturnType<typeof useToast>
	t: ReturnType<typeof useTranslation>['t']
}

// On the very first pickup, HandChip is still mounting and handTargetElAtom
// is still being populated. Poll across a handful of animation frames.
const PICKUP_TARGET_POLL_FRAMES = 6

function doPickUp(opts: PickUpOpts) {
	const ctxIdTag = opts.activeContextIdTag
	const items: FileHandItem[] = opts.selectedFiles.map((f) => ({
		type: 'file' as const,
		id: f.entryId,
		fileId: f.fileId,
		// The node holding the blob: the upstream node for a mirrored row, else the serving context
		idTag: f.upstream?.idTag ?? ctxIdTag,
		sourceContext: ctxIdTag,
		ownerIdTag: f.owner?.idTag,
		sourceParentId: opts.isManagedView
			? MANAGED_FOLDER_ID
			: !f.parentId || f.parentId === '__root__'
				? null
				: f.parentId,
		sourceChannel: f.channel ? absChannel(f.channel, ctxIdTag) : null,
		label: f.fileName,
		fileTp: f.fileTp,
		contentType: f.contentType,
		brokenAt: f.brokenAt,
		inTrash: opts.inTrash,
		// The same substitution the menu makes above, but per row so a mixed multi-select is
		// judged correctly: a mirrored row's cached `accessLevel` is the ACTIVE context's
		// answer about a row the upstream node decides, and `scopeRoles` is empty there, so
		// judging the two together would read a foreign grant as ours. See `resolveAccessLevel`.
		writable: canWrite(
			resolveAccessLevel(
				isCrossOwnerFile(f.upstream?.idTag, false) ? { ...f, accessLevel: undefined } : f,
				opts.authIdTag,
				opts.scopeRoles
			)
		)
	}))

	const reduced = prefersReducedMotion()

	// Briefly lift each source row to telegraph the pickup. Safe to add to
	// elements that have already been removed (no-op on null).
	if (!reduced) {
		for (const it of items) {
			const sel = `[data-file-id="${CSS.escape(it.id)}"][data-source-context="${CSS.escape(it.sourceContext)}"]`
			const el =
				document.querySelector(sel) ??
				document.querySelector(`[data-file-id="${CSS.escape(it.id)}"]`)
			if (el instanceof HTMLElement) {
				el.classList.add('cl-pickup-lift')
				window.setTimeout(() => el.classList.remove('cl-pickup-lift'), 260)
			}
		}
	}

	try {
		pickUp(opts.store.get, opts.store.set, items)
		opts.toast.success(opts.t('Picked up {{count}} files', { count: items.length }))
		if (!reduced) {
			// On the very first pickup the HandChip is mounting *now* — its ref
			// callback hasn't populated handTargetElAtom yet. Poll across a few
			// animation frames so the parcel flight starts as soon as the icon
			// node is available.
			const tryFly = (tries: number) => {
				const target = opts.store.get(handTargetElAtom)
				if (target) {
					void fly({ items, anchor: target, dir: 'to', reducedMotion: false })
				} else if (tries > 0) {
					requestAnimationFrame(() => tryFly(tries - 1))
				}
			}
			tryFly(PICKUP_TARGET_POLL_FRAMES)
		}
	} catch (err) {
		if (err instanceof HandTypeConflictError) {
			opts.toast.warning(opts.t('Hand contains a different item type. Empty the hand first.'))
		} else {
			throw err
		}
	}
}

export interface ContextMenuProps {
	selectedFiles: File[]
	clickedFile: File
	position: ContextMenuPosition
	viewMode: ViewMode
	fileOps: FileOps
	onClose: () => void
	onShare?: (file: File) => void
	isRemoteBrowsing?: boolean
}

export function ContextMenu({
	selectedFiles,
	clickedFile,
	position,
	viewMode,
	fileOps,
	onClose,
	onShare,
	isRemoteBrowsing
}: ContextMenuProps) {
	const { t } = useTranslation()
	const [auth] = useAuth()
	const contextIdTag = useCurrentContextIdTag()
	const [activeContext] = useAtom(activeContextAtom)
	const store = useStore()
	const toast = useToast()

	// Detect mobile for ActionSheet vs Menu
	const isMobile = useIsMobile()

	const count = selectedFiles.length
	const isSingleSelect = count === 1
	const file = isSingleSelect ? selectedFiles[0] : clickedFile
	const isFolder = file.fileTp === 'FLDR'
	const isTrashView = viewMode === 'trash'
	const isManagedView = viewMode === 'managed'

	// Starred/Pinned state for single/multi selection
	const allStarred = selectedFiles.every((f) => f.userData?.starred)
	const allPinned = selectedFiles.every((f) => f.userData?.pinned)
	const isStarred = isSingleSelect ? (file.userData?.starred ?? false) : allStarred
	const isPinned = isSingleSelect ? (file.userData?.pinned ?? false) : allPinned

	const handleAction = (action: () => void) => () => {
		action()
		onClose()
	}

	const selectedFileIds = selectedFiles.map((f) => f.entryId)

	// Get handlers for "Open with" submenu
	const handlers = isSingleSelect && !isFolder ? getHandlersForContentType(file.contentType) : []
	// Collect all launch modes from all handlers
	const launchModeEntries = handlers.flatMap((h) =>
		(h.manifest.launchModes ?? []).map((mode) => ({
			manifest: h.manifest,
			mode
		}))
	)
	// Show "Open with" when multiple handlers exist or there are launch modes
	const showOpenWith = handlers.length > 1 || launchModeEntries.length > 0

	/*
	 * `useFileOwnerScope` is the authority on sharing standing, and the menu deliberately does NOT
	 * mount it: the hook fetches a proxy token per file, and doing that on every right-click /
	 * long-press would widen an identity disclosure rather than narrow one. So the menu
	 * APPROXIMATES it from data already in hand, conservatively — withholding the ACTIVE context's
	 * roles on a cross-owner row, since the `leader` short-circuit inside canManageShares is judged
	 * against the node that SERVES the row.
	 */
	const isCrossOwner = isCrossOwnerFile(file.upstream?.idTag, false)
	// The same substitution useFileOwnerScope makes, so the menu's approximation can only be
	// narrower than what the dialog will allow, never wider: on a mirrored row drop the ACTIVE
	// context's cached accessLevel, which is not the upstream node's answer (an 'A' grant recorded
	// locally would otherwise open the full share UI, then 403).
	const scopedFile = isCrossOwner ? { ...file, accessLevel: undefined } : file
	const scopeRoles = isCrossOwner ? [] : (activeContext?.roles ?? [])
	const canShare = isSingleSelect && canManageShares(scopedFile, auth?.idTag, scopeRoles)
	const canManage = isSingleSelect && canManageFile(scopedFile, auth?.idTag, scopeRoles)
	const currentVisibility = getVisibilityOption(t, file.visibility ?? null)
	const visibilityDropdownOptions = getVisibilityDropdownOptions(t)

	// Use different components based on mobile/desktop
	const Item = isMobile ? ActionSheetItem : MenuItem
	const Divider = isMobile ? ActionSheetDivider : MenuDivider
	const Sub = isMobile ? ActionSheetSubItem : SubMenuItem

	const renderPickUpItem = (inTrash: boolean) =>
		activeContext && (
			<Item
				icon={<IcHand />}
				label={count > 1 ? t('Pick up {{count}} files', { count }) : t('Pick up')}
				onClick={handleAction(() =>
					doPickUp({
						activeContextIdTag: activeContext.idTag,
						authIdTag: auth?.idTag,
						scopeRoles,
						selectedFiles,
						inTrash,
						isManagedView,
						store,
						toast,
						t
					})
				)}
			/>
		)

	const menuContent = isTrashView ? (
		// Trash view menu
		<>
			<Item
				icon={<IcRestore />}
				label={isSingleSelect ? t('Restore') : t('Restore {{count}} items', { count })}
				onClick={handleAction(() =>
					isSingleSelect
						? fileOps.doRestoreFile?.(file.entryId)
						: fileOps.doRestoreFiles?.(selectedFileIds)
				)}
			/>
			{renderPickUpItem(true)}
			<Divider />
			<Item
				icon={<IcDeleteForever />}
				label={
					isSingleSelect
						? t('Delete permanently')
						: t('Delete {{count}} items permanently', { count })
				}
				onClick={handleAction(() =>
					isSingleSelect
						? fileOps.doPermanentDeleteFile?.(file.entryId)
						: fileOps.doPermanentDeleteFiles?.(selectedFileIds)
				)}
				danger
			/>
		</>
	) : (
		// Normal view menu
		<>
			{/* Open - only for single selection */}
			{isSingleSelect && (
				<Item
					icon={<IcOpen />}
					label={isFolder ? t('Open folder') : t('Open')}
					onClick={handleAction(() =>
						fileOps.openFile(file.entryId, toAppAccess(file.accessLevel))
					)}
				/>
			)}

			{/* View in read-only mode - only for files user can edit */}
			{isSingleSelect && !isFolder && canWrite(file.accessLevel) && (
				<Item
					icon={<IcView />}
					label={t('View')}
					onClick={handleAction(() => fileOps.openFile(file.entryId, 'read'))}
				/>
			)}

			{/* Open with - when multiple handlers or launch modes exist */}
			{isSingleSelect && showOpenWith && (
				<Sub icon={<IcOpenWith />} label={t('Open with...')}>
					{handlers.map((h) => {
						const AppIcon = getIcon(h.manifest.icon)
						return (
							<Item
								key={h.manifest.id}
								icon={AppIcon ? <AppIcon /> : undefined}
								label={h.manifest.name}
								onClick={handleAction(() =>
									fileOps.openFileWithApp?.(
										file.entryId,
										h.manifest.id,
										toAppAccess(file.accessLevel)
									)
								)}
							/>
						)
					})}
					{launchModeEntries.length > 0 && <Divider />}
					{launchModeEntries.map((entry) => {
						const AppIcon = getIcon(entry.manifest.icon)
						return (
							<Item
								key={`${entry.manifest.id}-${entry.mode.id}`}
								icon={AppIcon ? <AppIcon /> : undefined}
								label={`${entry.manifest.name}: ${entry.mode.label}`}
								onClick={handleAction(() => {
									fileOps.openFileWithApp?.(
										file.entryId,
										entry.manifest.id,
										toAppAccess(file.accessLevel),
										'mode=' + entry.mode.id
									)
								})}
							/>
						)
					})}
				</Sub>
			)}

			{/* Download - only for single file (not folder) */}
			{isSingleSelect && !isFolder && auth?.idTag && (
				<Item
					icon={<IcDownload />}
					label={t('Download')}
					onClick={handleAction(() => {
						if (isFileProcessing(file)) {
							toast.warning(
								t(
									'This file is still being processed — please try again in a moment.'
								)
							)
							return
						}
						const idTag = contextIdTag ?? auth?.idTag
						if (idTag && file.fileId)
							triggerFileDownload(idTag, file.fileId, file.fileName, () =>
								toast.error(t('Download failed. Please try again.'))
							)
					})}
				/>
			)}

			{/* Pick up (non-trash views; the trash variant renders above) */}
			{!isTrashView && renderPickUpItem(false)}

			{/* Share & Visibility section — hidden in remote browsing */}
			{!isRemoteBrowsing &&
				!isManagedView &&
				((onShare && canShare) ||
					(!isCrossOwner && canManage && fileOps.setVisibility)) && <Divider />}

			{/* Share - only for single selection, and only with share-manager standing */}
			{!isRemoteBrowsing && !isManagedView && canShare && onShare && (
				<Item
					icon={<IcShare />}
					label={t('Share...')}
					onClick={handleAction(() => onShare(file))}
				/>
			)}

			{/* Visibility submenu - only for single selection and owner/manager. Never for a
			    cross-owner row: the menu holds no owner-scoped client to route the write to, so it
			    would go to the local node. DetailsPanel, which does hold the scope, is the place to
			    change visibility on such a row. */}
			{!isRemoteBrowsing &&
				!isManagedView &&
				!isCrossOwner &&
				canManage &&
				fileOps.setVisibility && (
					<Sub
						icon={React.createElement(currentVisibility.icon)}
						label={t('Visibility')}
						detail={currentVisibility.label}
					>
						{visibilityDropdownOptions.map((opt) => {
							const VisibilityIcon = getVisibilityIcon(opt.value)
							const isCurrentVisibility = (file.visibility ?? null) === opt.value
							return (
								<Item
									key={opt.value ?? 'null'}
									icon={<VisibilityIcon />}
									label={opt.label}
									onClick={handleAction(() =>
										fileOps.setVisibility!(file.entryId, opt.value)
									)}
									disabled={isCurrentVisibility}
								/>
							)
						})}
					</Sub>
				)}

			{/* Star toggle — hidden in remote browsing */}
			{!isRemoteBrowsing && !isManagedView && (
				<Item
					icon={isStarred ? <IcUnstar /> : <IcStar />}
					label={
						isSingleSelect
							? isStarred
								? t('Remove star')
								: t('Add star')
							: isStarred
								? t('Remove star from {{count}} items', { count })
								: t('Add star to {{count}} items', { count })
					}
					onClick={handleAction(() =>
						isSingleSelect
							? fileOps.toggleStarred?.(file.entryId)
							: fileOps.toggleStarredBatch?.(selectedFileIds, !isStarred)
					)}
				/>
			)}

			{/* Pin toggle — hidden in remote browsing */}
			{!isRemoteBrowsing && !isManagedView && (
				<Item
					icon={isPinned ? <IcUnpin /> : <IcPin />}
					label={
						isSingleSelect
							? isPinned
								? t('Unpin')
								: t('Pin to top')
							: isPinned
								? t('Unpin {{count}} items', { count })
								: t('Pin {{count}} items to top', { count })
					}
					onClick={handleAction(() =>
						isSingleSelect
							? fileOps.togglePinned?.(file.entryId)
							: fileOps.togglePinnedBatch?.(selectedFileIds, !isPinned)
					)}
				/>
			)}

			{/* Refresh metadata - only for mirrored rows, whose canonical copy lives elsewhere */}
			{!isRemoteBrowsing && !isManagedView && isSingleSelect && file.upstream?.idTag && (
				<Item
					icon={<IcRefresh />}
					label={t('Refresh metadata')}
					onClick={handleAction(() => fileOps.doRefreshFile?.(file.entryId))}
				/>
			)}

			{!isRemoteBrowsing && !isManagedView && <Divider />}

			{/* Rename - only for single selection */}
			{!isRemoteBrowsing && !isManagedView && isSingleSelect && (
				<Item
					icon={<IcRename />}
					label={t('Rename')}
					onClick={handleAction(() => fileOps.renameFile(file.entryId))}
				/>
			)}

			{/* Duplicate - only for single CRDT/RTDB rows that originate here. A mirrored row
			    would create an empty record because content (CRDT/RTDB data) lives on the
			    upstream node and is not transferred. */}
			{!isRemoteBrowsing &&
				!isManagedView &&
				isSingleSelect &&
				(file.fileTp === 'CRDT' || file.fileTp === 'RTDB') &&
				!file.upstream?.idTag && (
					<Item
						icon={<IcDuplicate />}
						label={t('Duplicate')}
						onClick={handleAction(() => fileOps.doDuplicateFile?.(file.entryId))}
					/>
				)}

			{!isRemoteBrowsing && !isManagedView && <Divider />}

			{/* Delete — hidden in remote browsing */}
			{!isRemoteBrowsing && !isManagedView && (
				<Item
					icon={<IcTrash />}
					label={
						isSingleSelect
							? t('Move to trash')
							: t('Move {{count}} items to trash', { count })
					}
					onClick={handleAction(() =>
						isSingleSelect
							? fileOps.doDeleteFile(file.entryId)
							: fileOps.doDeleteFiles?.(selectedFileIds)
					)}
					danger
				/>
			)}
		</>
	)

	// Render ActionSheet on mobile, Menu on desktop
	if (isMobile) {
		return (
			<ActionSheet
				isOpen={true}
				onClose={onClose}
				title={isSingleSelect ? file.fileName : t('{{count}} items selected', { count })}
			>
				{menuContent}
			</ActionSheet>
		)
	}

	return (
		<Menu position={position} onClose={onClose}>
			{menuContent}
		</Menu>
	)
}

// vim: ts=4
